<template>
  <PageTemplate>
    <Loader v-if="!isLoaded"/>
    <div v-else>
      <div class="d-flex flex-column flex-lg-row justify-content-lg-between align-items-lg-center">
        <h6 class="element-header mb-3 mb-lg-0">
          {{ $t('course.general_title') }}
        </h6>

        <div class="row g-2 w-100 w-lg-auto m-0 justify-content-end">
          <div class="col-12 col-sm-auto pr-3 pl-0">
            <CreateButton
                class="w-100"
                @click="handlePrimaryPriceModalForm()"
                :label="$t('course.socio_therapy_coaching.title_primary_type_price')"/>
          </div>

          <div class="col-12 col-sm-auto p-0 mt-3 mt-sm-0">
            <CreateButton
                class="w-100"
                @click="handleSubstitutePriceModalForm()"
                :label="$t('course.socio_therapy_coaching.title_substitute_type_price')"/>
          </div>
        </div>
      </div>

      <div class="element-box mt-3">
        <h5 class="form-header mb-4">
          {{ $t('course.socio_therapy_coaching.list') }}
        </h5>

        <Search
            :filters="filters"
            :loading="isSearching"
            :operationTypeDropdown="operationTypeDropdown"
            :priceTypeDropdown="priceTypeDropdown"
            @search="getPriceList"/>


        <PerPage :values="[10, 25, 50, 100]" @update:perPage="getPriceList"/>

        <div class="table-responsive overflow-auto pt-4">
          <table class="table table-striped">
            <thead>
            <tr>
              <th>{{ $t('course.socio_therapy_coaching.operation_type') }}</th>
              <th>{{ $t('course.socio_therapy_coaching.price_type') }}</th>
              <th>{{ $t('course.socio_therapy_coaching.start_from') }}</th>
              <th>{{ $t('course.socio_therapy_coaching.valid_till') }}</th>
              <th>{{ $t('course.socio_therapy_coaching.price') }}</th>
              <th>{{ $t('course.socio_therapy_coaching.half_price') }}</th>
              <th>{{ $t('course.socio_therapy_coaching.ten_minutes_price') }}</th>
              <th>{{ $t('course.socio_therapy_coaching.price_description') }}</th>
              <th>{{ $t('course.socio_therapy_coaching.half_price_description') }}</th>
              <th>{{ $t('course.socio_therapy_coaching.ten_minutes_price_description') }}</th>
              <th style=" text-align: right; padding-right: 30px; min-width: 150px;">{{ $t('general.actions') }}</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(price) in priceList?.data" :key="price.id">
              <td>{{ price?.operationTypeTranslation }}</td>
              <td>{{ price?.priceTypeTranslation }}</td>
              <td>{{ price?.startFrom }}</td>
              <td>{{ price?.validTill }}</td>
              <td>{{ Number(price?.price).toFixed(2) }}</td>
              <td>
                  <span v-if="showHalfPrice(price.priceType)">
                    {{ Number(price?.halfPrice).toFixed(2) }}
                  </span>
              </td>
              <td>
                  <span v-if="showTenMinutesPricePrice(price.priceType)">
                    {{ Number(price?.tenMinutesPrice).toFixed(2) }}
                  </span>
              </td>
              <td>{{ price?.priceDescription }}</td>
              <td>
                  <span v-if="showHalfPrice(price.priceType)">
                    {{ price?.halfPriceDescription }}
                  </span>
              </td>
              <td>
                  <span v-if="showTenMinutesPricePrice(price.priceType)">
                    {{ price?.tenMinutesPriceDescription }}
                  </span>
              </td>
              <td class="text-right">
                <button class="btn btn-primary" v-if="price.operationType === 'sociotherapie_primary'"
                        @click="handlePrimaryPriceModalForm(price.id)">
                  <i class="os-icon os-icon-edit"></i>
                </button>
                <button class="btn btn-primary" v-if="price.operationType === 'sociotherapie'"
                        @click="handleSubstitutePriceModalForm(price.id)">
                  <i class="os-icon os-icon-edit"></i>
                </button>
                <button class="btn btn-danger ml-2"
                        @click="handleModalDelete(price.id!)">
                  <i class="os-icon os-icon-trash"></i>
                </button>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
        <Pagination
            v-if="priceList?.pagination?.total_pages ?? 0 > 1"
            :totalPages="priceList?.pagination?.total_pages"
            :currentPage="priceList?.pagination?.current_page"
            :totalItems="priceList?.pagination?.total_items"
            :perPage="priceList?.pagination?.items_per_page"
            @pagechanged="getPriceList"/>
      </div>
    </div>

    <Modal
        v-model="showPrimaryFormModal"
        :title="$t('course.socio_therapy_coaching.title_primary_type_price')"
        :hide-cancel="true"
        :button-label="$t('general.button.save')"
        :customWidth="'800px'"
        :hide-footer="true"
        classFooter="justify-content-center">
      <PrimaryTypePriceForm
          :priceId="priceId"
          :meetingTypeDropdown="primaryTypeDropdown"
          v-model:submitForm="submitForm"/>
    </Modal>

    <Modal
        v-model="showSubstituteFormModal"
        :title="$t('course.socio_therapy_coaching.title_substitute_type_price')"
        :hide-cancel="true"
        :button-label="$t('general.button.save')"
        :customWidth="'800px'"
        :hide-footer="true"
        classFooter="justify-content-center">
      <SubstituteTypePriceForm
          :priceId="priceId"
          :meetingTypeDropdown="substituteTypeDropdown"
          v-model:submitForm="submitForm"/>
    </Modal>

    <ModalDelete
        v-model="showDeleteModal"
        :header="t('general.delete_item_header')"
        :onDelete="deletePrice">
    </ModalDelete>

  </PageTemplate>
</template>


<script setup lang="ts">
import CreateButton from '@/components/CreateButton.vue';
import Loader from '@/components/Loader.vue';
import ModalDelete from '@/components/modal/ModalDelete.vue';
import PageTemplate from '@/components/PageTemplate.vue';
import Pagination from '@/components/Pagination.vue';
import PerPage from '@/components/PerPage.vue';
import {CloseToast, ErrorToast, SuccessToast} from '@/components/VueToast';
import {computed, onMounted, ref, watch} from 'vue';
import {useI18n} from 'vue-i18n';
import coursePriceCoachingService from "@/api/Course/SocioTherapyCoaching";
import Modal from "@/components/modal/Modal.vue";
import PrimaryTypePriceForm from "@/views/App/Course/SocioTherapyCoaching/PrimaryTypePriceForm.vue";
import {piniaEnumsStore} from "@/store/piniaEnumsStore";
import SubstituteTypePriceForm from "@/views/App/Course/SocioTherapyCoaching/SubstituteTypePriceForm.vue";
import {IFilters, IPriceList, ISelectOption} from "@/interfaces/Course/SocioTherapy/ICoursePrice";
import Search from "@/views/App/Course/SocioTherapyCoaching/Search.vue";

const piniaEnumsState = piniaEnumsStore();
const getEnums = computed(() => piniaEnumsState.enums);
const {t} = useI18n();
const isLoaded = ref(false);
const priceId = ref<number | null>(null)
const showPrimaryFormModal = ref(false);
const showSubstituteFormModal = ref(false);
const primaryTypeDropdown = ref<ISelectOption[]>([])
const substituteTypeDropdown = ref<ISelectOption[]>([])
const showDeleteModal = ref(false)
const submitForm = ref(false);
const isSearching = ref(false);
const operationTypeDropdown = ref<ISelectOption[]>([])
const priceTypeDropdown = ref<ISelectOption[]>([])

const filters = ref<IFilters>({
  operationType: '',
  priceType: '',
});


const priceList = ref<IPriceList>();

watch(submitForm, () => {
  priceId.value = null;
  showPrimaryFormModal.value = false;
  showSubstituteFormModal.value = false;
  showDeleteModal.value = false;
  getPriceList();
});

async function getPriceList(
    page: number | undefined = 1,
    perPage?: number,
    passedFilters?: IFilters
) {
  CloseToast();

  if (passedFilters) {
    isSearching.value = true;
  }

  const perPageRes = priceList.value?.pagination?.items_per_page;

  try {
    const {data} = await coursePriceCoachingService.getPriceList({
      page: page,
      limit: perPage ?? perPageRes,
      ...(passedFilters ? passedFilters : filters.value),
    });

    priceList.value = data;
    isSearching.value = false;
  } catch (error) {
    await ErrorToast(error);
  }
}

async function deletePrice() {
  if (priceId.value === null) return;

  try {
    await coursePriceCoachingService.deletePrice(+priceId.value);

    priceId.value = null;
    showDeleteModal.value = false;

    await getPriceList();
    SuccessToast(t('general.toasts.delete_success'));
  } catch (error) {
    await ErrorToast(error);
  }
}


function handlePrimaryPriceModalForm(id?: number) {
  CloseToast()
  priceId.value = id ?? null
  showPrimaryFormModal.value = true;
  showSubstituteFormModal.value = false;
  showDeleteModal.value = false;
}

function handleSubstitutePriceModalForm(id?: number) {
  CloseToast()
  priceId.value = id ?? null
  showSubstituteFormModal.value = true;
  showPrimaryFormModal.value = false;
  showDeleteModal.value = false;
}

function handleModalDelete(id: number) {
  CloseToast()
  priceId.value = id;
  showDeleteModal.value = true;
  showPrimaryFormModal.value = false;
  showSubstituteFormModal.value = false;
}

function generateMeetingTypeDropdown() {
  primaryTypeDropdown.value = [
    {label: "Hausbesuch", value: "home_visit"},
    ...getEnums.value.socioTherapyMeetingTypes
  ];

  substituteTypeDropdown.value = [
    {label: "Hausbesuch", value: "home_visit"},
    ...getEnums.value.socioTherapyMeetingTypes2
  ];
  // substituteTypeDropdown.value = getEnums.value.socioTherapyMeetingTypes2

  operationTypeDropdown.value = getEnums.value.socioTherapyOperationTypes
  priceTypeDropdown.value = [...primaryTypeDropdown.value, ...substituteTypeDropdown.value]
}

function showHalfPrice(priceType: string) {
  return [
    "outreaching",
    "outreaching_office",
    "video_call",
    "telephone",
    "sociotherapy",
    "initial_consultation"
  ].includes(priceType);
}

function showTenMinutesPricePrice(priceType: string) {
  return [
    "sociotherapy",
    "initial_consultation"
  ].includes(priceType);
}

onMounted(async () => {
  await Promise.all([
    getPriceList()
  ]);

  generateMeetingTypeDropdown();
  isLoaded.value = true;
});
</script>

<style scoped>

</style>